import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {SoftNavOnlyLink as RouterLink} from '@github-ui/react-router'
import {isUIService} from '@github-ui/runtime-environment'
import {Label, Link, Truncate} from '@primer/react'
import {LockIcon, RepoTemplateIcon} from '@primer/octicons-react'
import {useRef} from 'react'
import type {HeaderResponse, RepoPaths, RepoIdentity} from '../types'
import {DemoNotification} from './DemoNotification'

function RepoLeadingVisual({
  ownerLogin,
  ownerAvatarUrl,
  isPrivate,
  isTemplate,
  isOrgOwned,
}: Pick<RepoIdentity, 'ownerAvatarUrl' | 'isPrivate' | 'isTemplate'> & {ownerLogin: string; isOrgOwned?: boolean}) {
  if (isTemplate) {
    return isPrivate ? (
      <LockIcon className="color-fg-muted mr-2 d-none d-md-block flex-shrink-0" />
    ) : (
      <RepoTemplateIcon className="color-fg-muted mr-2 d-none d-md-block flex-shrink-0" />
    )
  }

  return (
    <GitHubAvatar
      src={ownerAvatarUrl}
      size={24}
      square={isOrgOwned ? true : false}
      alt={ownerLogin}
      className="mr-2 d-none d-md-block flex-shrink-0"
    />
  )
}

export function RepoSubtitle({repo, advisoryInfo}: Pick<HeaderResponse, 'repo' | 'advisoryInfo'>) {
  const {isFork, isMirror, isAdvisoryWorkspace, parentRepo, mirrorUrl} = repo
  const getHovercardAttributes = useGetHovercardAttributesForType()

  return (
    <>
      {isFork && parentRepo && (
        <div className="show-whenRegular">
          <span className="text-small lh-condensed-ultra no-wrap mt-1" data-testid="fork-parent">
            <span>forked from </span>
            <Link
              inline
              as={RouterLink}
              to={parentRepo.url}
              reloadDocument={!isUIService}
              {...getHovercardAttributes('repository', {
                owner: parentRepo.ownerLogin,
                repo: parentRepo.name,
              })}
            >
              {parentRepo.ownerLogin}/{parentRepo.name}
            </Link>
          </span>
        </div>
      )}
      {isMirror && mirrorUrl && (
        <div className="show-whenRegular">
          <div
            className="d-inline-flex flex-items-baseline text-small lh-condensed-ultra no-wrap mt-1"
            data-testid="mirror-source"
          >
            <span>mirrored from </span>
            <Truncate as={Link} href={mirrorUrl} title={mirrorUrl} maxWidth={600}>
              {mirrorUrl}
            </Truncate>
          </div>
        </div>
      )}
      {isAdvisoryWorkspace && advisoryInfo && (
        <div className="show-whenRegular">
          <span className="text-small lh-condensed-ultra no-wrap mt-1" data-testid="advisory-parent">
            <span>Parent repository: </span>
            <Link
              as={RouterLink}
              to={advisoryInfo.parentRepo.url}
              inline
              reloadDocument={!isUIService}
              {...getHovercardAttributes('repository', {
                owner: advisoryInfo.parentRepo.ownerLogin,
                repo: advisoryInfo.parentRepo.name,
              })}
            >
              {advisoryInfo.parentRepo.ownerLogin}/{advisoryInfo.parentRepo.name}
            </Link>
          </span>
        </div>
      )}
    </>
  )
}

export type RepoTitleProps = Pick<HeaderResponse, 'repo' | 'repoName' | 'ownerLogin' | 'showDemoNotification'> & {
  paths: Pick<RepoPaths, 'ownerProfilePath' | 'repoUrl'>
  isOrgOwned?: boolean
}

/** Repository title with owner/name links, visibility badge, and fork parent info. */
export function RepoTitle({repo, repoName, paths, ownerLogin, isOrgOwned, showDemoNotification}: RepoTitleProps) {
  const {ownerAvatarUrl, isPrivate, isTemplate, visibilityLabel} = repo
  const labelVariant = visibilityLabel === 'Public archive' ? 'attention' : 'secondary'
  const repoTitleRef = useRef<HTMLDivElement>(null)

  return (
    <div id="repo-title-component" ref={repoTitleRef}>
      <div className="d-flex flex-items-center f3 text-normal min-width-0">
        <RepoLeadingVisual
          ownerLogin={ownerLogin}
          ownerAvatarUrl={ownerAvatarUrl}
          isPrivate={isPrivate}
          isTemplate={isTemplate}
          isOrgOwned={isOrgOwned}
        />
        <Truncate
          inline
          maxWidth="100%"
          title={repoName}
          itemProp="name"
          data-testid="repo-name-wrapper"
          className="mr-2"
        >
          <strong>
            <Link as={RouterLink} to={paths.repoUrl} className="color-fg-default" data-testid="repo-name-link">
              {repoName}
            </Link>
          </strong>
        </Truncate>
        <Label variant={labelVariant} className="mr-1 v-align-middle" data-testid="repo-visibility-label">
          {visibilityLabel}
        </Label>
      </div>
      {showDemoNotification && repo.ownerId && (
        <DemoNotification anchorRef={repoTitleRef} repoName={repoName} orgName={ownerLogin} ownerId={repo.ownerId} />
      )}
    </div>
  )
}
